import React from 'react';
import { Link } from 'react-router-dom';

const GCCCountry: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-teal-600 to-teal-700 text-white py-20">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            GCC Nursing Pathways
          </h1>
          <p className="text-xl md:text-2xl text-teal-100">
            Saudi Arabia • UAE • Qatar • Kuwait • Oman • Bahrain
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto space-y-12">
          {/* Intro */}
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed">
              GCC employers value clear documentation and verified experience. We help you avoid common rejections and speed approvals.
            </p>
          </div>

          {/* Popular Routes */}
          <div>
            <h2 className="text-2xl font-bold text-navy mb-4">Popular routes</h2>
            <ul className="space-y-2 text-slate-700">
              <li className="flex items-start">
                <span className="text-teal-600 mr-2">•</span>
                <span>Saudi Arabia • UAE • Qatar • Kuwait • Oman • Bahrain</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-600 mr-2">•</span>
                <span>Prometric/HAAD/DHA exams → Dataflow/TrueProfile → Good Standing → Offer</span>
              </li>
            </ul>
          </div>

          {/* Country Specifics Accordion */}
          <div>
            <h2 className="text-2xl font-bold text-navy mb-4">Country specifics (high-level)</h2>
            <div className="space-y-3">
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-navy hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Saudi Arabia / UAE
                  <span className="text-teal-600 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700 space-y-2">
                  <p><strong>Path:</strong> Prometric/DHA exams → Dataflow → facility interview → offer.</p>
                  <p><strong>Tip:</strong> Keep employment letters and Good Standing current.</p>
                </div>
              </details>
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-navy hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Qatar • Kuwait • Oman • Bahrain
                  <span className="text-teal-600 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700 space-y-2">
                  <p><strong>Similar flow:</strong> Exam + Dataflow + verification.</p>
                  <p><strong>Note:</strong> Experience requirements vary by facility and role.</p>
                </div>
              </details>
            </div>
          </div>

          {/* Typical Timeline */}
          <div>
            <h2 className="text-2xl font-bold text-navy mb-4">Typical timeline</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-32 font-semibold text-teal-600">Document prep</div>
                <div className="text-slate-700">Gather employment letters, Good Standing, transcripts</div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-32 font-semibold text-teal-600">Exam & Dataflow</div>
                <div className="text-slate-700">Sit exam; submit Dataflow/TrueProfile</div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-32 font-semibold text-teal-600">Verification</div>
                <div className="text-slate-700">Follow up on any additional requests</div>
              </div>
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0 w-32 font-semibold text-teal-600">Offer & onboarding</div>
                <div className="text-slate-700">Contract, medicals, travel</div>
              </div>
            </div>
          </div>

          {/* FAQ */}
          <div>
            <h2 className="text-2xl font-bold text-navy mb-4">Frequently Asked Questions</h2>
            <div className="space-y-3">
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-navy hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Do I need previous Gulf experience?
                  <span className="text-teal-600 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700">
                  Not always. Requirements differ by hospital and specialty. Verified documentation helps most.
                </div>
              </details>
              <details className="group bg-white border border-slate-200 rounded-lg overflow-hidden">
                <summary className="cursor-pointer p-4 font-semibold text-navy hover:bg-slate-50 transition-colors flex justify-between items-center">
                  Can I apply while my Dataflow is in progress?
                  <span className="text-teal-600 group-open:rotate-180 transition-transform">▼</span>
                </summary>
                <div className="p-4 pt-0 text-slate-700">
                  Yes. Many employers proceed in parallel if your documentation is underway.
                </div>
              </details>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="bg-teal-50 rounded-lg p-8 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/signup"
                className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-700 transition-colors text-center"
              >
                Register Free
              </Link>
              <Link
                to="/resources"
                className="inline-block bg-white text-teal-600 border-2 border-teal-600 px-8 py-4 rounded-lg font-semibold text-lg hover:bg-teal-50 transition-colors text-center"
              >
                See Resources
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default GCCCountry;
